<template>
  <div class="gtc-container" aria-live="polite" aria-atomic="false">
    <TransitionGroup name="gtc-slide" tag="div" class="gtc-stack">
      <div
        v-for="toast in visibleToasts"
        :key="toast.id"
        class="gtc-toast"
        :class="`gtc-toast--${toast.type}`"
        role="alert"
        @mouseenter="pauseToast(toast.id)"
        @mouseleave="resumeToast(toast.id)"
      >
        <!-- Faixa colorida lateral -->
        <div class="gtc-toast__accent"></div>

        <!-- Ícone -->
        <div class="gtc-toast__icon">
          <va-icon :name="typeConfig[toast.type].icon" size="1.2rem" />
        </div>

        <!-- Texto -->
        <div class="gtc-toast__body">
          <p class="gtc-toast__type-label">{{ typeConfig[toast.type].label }}</p>
          <p class="gtc-toast__message">{{ toast.message }}</p>
        </div>

        <!-- Fechar -->
        <button class="gtc-toast__close" aria-label="Fechar" @click="removeToast(toast.id)">
          <va-icon name="close" size="0.85rem" />
        </button>

        <!-- Barra de countdown — usa --color-brand-accent do cliente -->
        <div
          class="gtc-toast__countdown"
          :style="{
            animationDuration: toast.duration + 'ms',
            animationPlayState: toast.paused ? 'paused' : 'running',
          }"
        ></div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { toastState, removeToast, pauseToast, resumeToast } from '@/services/toastService'

const { t } = useI18n()

// Máximo 5 toasts visíveis; mais recentes no topo
const visibleToasts = computed(() =>
  [...toastState.toasts].reverse().slice(0, 5)
)

const typeConfig = computed(() => ({
  success: {
    icon: 'check_circle',
    label: t('toast.type_success'),
  },
  error: {
    icon: 'cancel',
    label: t('toast.type_error'),
  },
  warning: {
    icon: 'warning',
    label: t('toast.type_warning'),
  },
  info: {
    icon: 'info',
    label: t('toast.type_info'),
  },
}))
</script>

<style scoped>
/* ── Container fixo, canto superior direito ─────────────────── */
.gtc-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  pointer-events: none;
}

.gtc-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

/* ── Card base ───────────────────────────────────────────────── */
.gtc-toast {
  pointer-events: all;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 14px;
  padding: 14px 40px 20px 14px;
  min-width: 290px;
  max-width: 400px;
  overflow: hidden;
  animation: gtc-card-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  /* Cores definidas por tema abaixo */
}

/* ── Faixa lateral — cor por tipo ───────────────────────────── */
.gtc-toast__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 14px 0 0 14px;
}

.gtc-toast--success .gtc-toast__accent { background: #10b981; }
.gtc-toast--error   .gtc-toast__accent { background: #ef4444; }
.gtc-toast--warning .gtc-toast__accent { background: #f59e0b; }
.gtc-toast--info    .gtc-toast__accent { background: #3b82f6; }

/* ── Ícone ──────────────────────────────────────────────────── */
.gtc-toast__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 1.2rem;
}

.gtc-toast--success .gtc-toast__icon { color: #10b981; background: #ecfdf5; }
.gtc-toast--error   .gtc-toast__icon { color: #ef4444; background: #fef2f2; }
.gtc-toast--warning .gtc-toast__icon { color: #f59e0b; background: #fffbeb; }
.gtc-toast--info    .gtc-toast__icon { color: #3b82f6; background: #eff6ff; }

/* ── Texto ──────────────────────────────────────────────────── */
.gtc-toast__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gtc-toast__type-label {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.gtc-toast__message {
  margin: 0;
  font-size: 0.84rem;
  font-weight: 500;
  line-height: 1.45;
  word-break: break-word;
}

.gtc-toast--success .gtc-toast__type-label { color: #10b981; }
.gtc-toast--error   .gtc-toast__type-label { color: #ef4444; }
.gtc-toast--warning .gtc-toast__type-label { color: #f59e0b; }
.gtc-toast--info    .gtc-toast__type-label { color: #3b82f6; }

/* ── Botão fechar ───────────────────────────────────────────── */
.gtc-toast__close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 4px;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
}

/* ── Countdown — cor dos indicadores /count do cliente ──────── */
.gtc-toast__countdown {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: var(--color-brand-accent, #fa2b00);
  border-radius: 0 0 14px 14px;
  animation: gtc-countdown linear forwards;
  opacity: 0.85;
}

/* ── Animações ──────────────────────────────────────────────── */
@keyframes gtc-card-in {
  from { opacity: 0; transform: translateX(60px) scale(0.92); }
  to   { opacity: 1; transform: translateX(0)    scale(1);    }
}

@keyframes gtc-countdown {
  from { width: 100%; }
  to   { width: 0%;   }
}

/* ── Transições da transition-group ─────────────────────────── */
.gtc-slide-enter-active {
  transition: opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.gtc-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.gtc-slide-enter-from {
  opacity: 0;
  transform: translateX(60px) scale(0.92);
}
.gtc-slide-leave-to {
  opacity: 0;
  transform: translateX(60px) scale(0.88);
}
.gtc-slide-move {
  transition: transform 0.3s ease;
}
</style>

<!-- Tema claro e escuro — não scoped para responder à classe do <html> -->
<style>
/* ── Tema claro ──────────────────────────────────────────────── */
html.theme-light .gtc-toast {
  background: #EFF0F5;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.12);
}
html.theme-light .gtc-toast__message {
  color: #1e293b;
}
html.theme-light .gtc-toast__close {
  color: #64748b;
}
html.theme-light .gtc-toast__close:hover {
  color: #1e293b;
  background: rgba(0, 0, 0, 0.08);
}

/* ── Tema escuro ─────────────────────────────────────────────── */
html.theme-dark .gtc-toast {
  background: rgb(18, 18, 18);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.3);
}
html.theme-dark .gtc-toast__message {
  color: #e8e8e8;
}
html.theme-dark .gtc-toast__close {
  color: #6b7280;
}
html.theme-dark .gtc-toast__close:hover {
  color: #e8e8e8;
  background: rgba(255, 255, 255, 0.08);
}

/* Ícones no tema escuro — fundo levemente mais suave */
html.theme-dark .gtc-toast--success .gtc-toast__icon { background: rgba(16,185,129,0.12); }
html.theme-dark .gtc-toast--error   .gtc-toast__icon { background: rgba(239,68,68,0.12);  }
html.theme-dark .gtc-toast--warning .gtc-toast__icon { background: rgba(245,158,11,0.12); }
html.theme-dark .gtc-toast--info    .gtc-toast__icon { background: rgba(59,130,246,0.12); }
</style>
